import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { WordCount, checkWordCount } from 'components/base/WordCount';
import { handleFieldError, moveToFirstError } from 'libs/api/errorHandle';
import { mentorFinderCreate } from 'libs/api/main';
import { showNotification } from 'libs/utils';
import { TMentorFinder } from 'modules/main';
import { CWindowWidth } from 'constants/base.const';
import { ENotificationType } from 'enums/base.enum';
import { Col, Row, Form, List, Input, Button, Typography, Tag } from 'antd';
import styled from 'styled-components';
import { FormWrapper } from 'GlobalStyles';
import BannerType03 from 'assets/images/banners/3.webp';
import ListStyleInfo from 'assets/Vector.svg';

const { Text, Title } = Typography;
const maxWidth = CWindowWidth - 300;
const CSLabMentorFinderWrapper = styled.div`
  .title_wrapper {
    width: 100%;
    height: 300px;
    background-image: url(${BannerType03});
    background-size: cover;
    padding: 100px 10px;
    .ant-typography {
      width: 100%;
      max-width: ${maxWidth}px;
      color: #ffffff;
      margin: auto;
      display: block;
    }
  }

  .intro {
    margin-top: 8px;
    font-size: 40px;
    font-weight: bold;
    line-height: 48px;
    color: #14112a;
  }
  .intro_list {
    margin-top: 24px;
    padding-right: 40px;
  }
  .intro_list_item {
    padding: 5px 0;
  }
  .intro_details {
    color: #14112a;
  }
`;

const CSLabMentorFinder = () => {
  const [form] = Form.useForm();
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);

  function onClickFindMentor() {
    form
      .validateFields()
      .then((value) => {
        handleFindMentor(value as TMentorFinder);
      })
      .catch((errorInfo) => {
        moveToFirstError(form, errorInfo.errorFields);
      });
  }

  async function handleFindMentor(value: TMentorFinder) {
    try {
      setSubmitButtonDisabled(true);
      const response = await mentorFinderCreate({
        company_name: value.company_name,
        purpose: value.purpose,
        requirement: value.requirement,
      });

      showNotification(
        ENotificationType.Success,
        '신청해주셔서 감사합니다. 영업일 기준 5일 내로 답변 드리겠습니다.',
      );
      form.resetFields();
    } catch (e) {
      handleFieldError(e, form);
      throw e;
    } finally {
      setSubmitButtonDisabled(false);
    }
  }

  return (
    <CSLabMentorFinderWrapper>
      <Helmet>
        <title>멘토 Finder</title>
      </Helmet>
      <div className='title_wrapper'>
        <Title> 멘토 Finder </Title>
        <Text style={{ marginTop: '10px', fontSize: '16px' }}> 새로운 멘토를 찾아드리는 서비스 입니다. </Text>
      </div>
      <div style={{ padding: '0px 10px' }}>
        <Row
          style={{
            maxWidth: `${maxWidth}px`,
            margin: '20px auto',
            padding: '0px',
          }}
          gutter={[40, 16]}
        >
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Tag color='#fbbc05'> BETA </Tag>
            <div className='intro'>
              {' '}
              전·현직 멘토를
              <br /> 만나고 싶은 <br /> 회사를 알려주세요.
            </div>
            <List className='intro_list' split={false}>
              <List.Item className='intro_list_item'>
                <List.Item.Meta
                  avatar={<img src={ListStyleInfo} />}
                  description={
                    <div className='intro_details'>
                      멘토 Finder는 Career Step의 멘토 pool 에 원하시는 멘토가 없는 경우, 새로운 멘토를 영업일
                      기준 5일 내로 찾아드리는 서비스입니다.
                    </div>
                  }
                />
              </List.Item>
              <List.Item className='intro_list_item'>
                <List.Item.Meta
                  avatar={<img src={ListStyleInfo} />}
                  description={
                    <div className='intro_details'>
                      현재는 전략 컨설팅, IB, PE, 외국계 기업 국내외 오피스 출신의 전·현직 멘토에 한정하여
                      찾아드리고 있습니다.
                    </div>
                  }
                />
              </List.Item>
              <List.Item className='intro_list_item'>
                <List.Item.Meta
                  avatar={<img src={ListStyleInfo} />}
                  description={
                    <div className='intro_details'>
                      원하시는 회사의 멘토를 찾기 어려울 수 있으며, 섭외 성사 및 멘토링 가능 여부는 이메일로
                      알려드립니다.
                    </div>
                  }
                />
              </List.Item>
            </List>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <FormWrapper>
              <Form form={form} layout='vertical' className='form-normal'>
                <Form.Item label='회사명'>
                  <Input.Group>
                    <Form.Item
                      name='company_name'
                      className='form-item no-margin'
                      rules={[{ required: true }]}
                    >
                      <Input
                        name='company_name'
                        placeholder='회사명을 입력해주세요.'
                        maxLength={20}
                        onChange={checkWordCount}
                      />
                    </Form.Item>
                    <WordCount target='company_name' maxCount={20} />
                  </Input.Group>
                </Form.Item>
                <Form.Item label='목적'>
                  <Input.Group>
                    <Form.Item name='purpose' className='form-item no-margin' rules={[{ required: true }]}>
                      <Input
                        name='purpose'
                        placeholder='해당 회사 멘토를 찾는 목적을 알려주세요.'
                        maxLength={100}
                        onChange={checkWordCount}
                      />
                    </Form.Item>
                    <WordCount target='purpose' maxCount={100} />
                  </Input.Group>
                </Form.Item>
                <Form.Item label='추가 요청 사항'>
                  <Input.Group>
                    <Form.Item name='requirement' className='form-item no-margin'>
                      <Input.TextArea
                        className='form_textarea'
                        placeholder='전·현직 구분, 직무, 학력, 성별 등 구체적인 희망사항이 있다면 적어주세요. 최대한 맞는 멘토를 찾아드리겠습니다.'
                        maxLength={300}
                        rows={8}
                        name='requirement'
                        onChange={checkWordCount}
                      />
                    </Form.Item>
                    <WordCount target='requirement' maxCount={300} />
                  </Input.Group>
                </Form.Item>
                <Form.Item shouldUpdate={true} className='form-button'>
                  {() => (
                    <Button
                      type='primary'
                      size='large'
                      htmlType='submit'
                      loading={submitButtonDisabled}
                      onClick={onClickFindMentor}
                    >
                      제출
                    </Button>
                  )}
                </Form.Item>
              </Form>
            </FormWrapper>
          </Col>
        </Row>
      </div>
    </CSLabMentorFinderWrapper>
  );
};

export default CSLabMentorFinder;
